import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ChildSelector from '../../components/parent/ChildSelector';
import { useParentChild } from '../../contexts/ParentChildContext';
import api from '../../services/api';

const STATUS_LABELS = {
  pending: { label: 'En attente', class: 'bg-slate-100 text-slate-600' },
  due_soon: { label: 'Échéance proche', class: 'bg-blue-100 text-blue-800' },
  partially_paid: { label: 'Partiel', class: 'bg-yellow-100 text-yellow-800' },
  paid: { label: 'Payé', class: 'bg-green-100 text-green-800' },
  overdue: { label: 'En retard', class: 'bg-red-100 text-red-800' },
};

const formatAmount = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' FCFA';

const ParentFeesPage = () => {
  const { inscriptionId } = useParentChild();
  const [installments, setInstallments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inscriptionId) return;
    setLoading(true);
    api.get(`/parent-portal/children/${inscriptionId}/fees`)
      .then((res) => {
        if (res.data.success) {
          setInstallments(res.data.data);
          setSummary(res.data.summary);
        }
      })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [inscriptionId]);

  return (
    <div className="space-y-4">
      <ChildSelector />
      <div>
        <h2 className="text-xl font-bold text-slate-900">Scolarité</h2>
        <p className="text-sm text-slate-500">Statut des paiements et échéancier</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total dû', value: summary.total_due, color: 'text-slate-900' },
            { label: 'Payé', value: summary.total_paid, color: 'text-green-600' },
            { label: 'Reste à payer', value: summary.outstanding, color: 'text-amber-600' },
            { label: 'En retard', value: summary.overdue_amount, color: 'text-red-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-sm font-bold mt-1 ${s.color}`}>{formatAmount(s.value)}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : installments.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500">Aucun échéancier configuré</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Échéance</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Date</th>
                <th className="text-right px-4 py-3">Montant</th>
                <th className="text-right px-4 py-3 hidden sm:table-cell">Payé</th>
                <th className="text-center px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {installments.map((inst) => {
                const st = STATUS_LABELS[inst.status] || STATUS_LABELS.pending;
                return (
                  <tr key={inst.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{inst.label}</p>
                      <p className="text-xs text-slate-400 sm:hidden">{inst.due_date}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">
                      {inst.due_date ? new Date(inst.due_date).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatAmount(inst.amount_due)}</td>
                    <td className="px-4 py-3 text-right text-green-600 hidden sm:table-cell">{formatAmount(inst.amount_paid)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.class}`}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ParentFeesPage;
